import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";
import { createApiClient } from "../api/ApiClient";

export type BillingInterval = "day" | "week" | "month" | "year";

export type SubscriptionStatus = "active" | "paused" | "cancelled";

export interface ProductSubscriber {
  id: string;
  status: SubscriptionStatus;
  /** Active but not yet paying: the first recurring charge is due on trial_ends_at. */
  in_trial: boolean;
  trial_ends_at: string | null;
  billing_date: string | null;
  created_at: string;
  forename: string;
  surname: string;
  email: string;
  delivery_address: string | null;
  amount: number;
  currency: string;
  billing_interval: BillingInterval;
  billing_interval_count: number;
}

export interface ProductPriceDetail {
  id: string;
  product_id: string;
  amount: number;
  currency: string;
  billing_interval: BillingInterval;
  billing_interval_count: number;
  trial_days: number | null;
  /** Major units, like `amount`; null for a free trial. */
  trial_amount: number | null;
  created_at: string;
  updated_at: string;
}

export interface ProductDetail {
  id: string;
  name: string;
  description: string;
  active: boolean;
  requires_delivery_address: boolean;
  created_at: string;
  updated_at: string;
  prices: ProductPriceDetail[];
  total_subscribers: number;
  /** How many of total_subscribers are still on their trial. */
  trial_subscribers: number;
  billing_period_revenue: number;
  subscribers: ProductSubscriber[];
}

export function useProduct(productId: string) {
  const { getToken } = useAuth();
  const api = createApiClient(getToken);

  return useQuery({
    queryKey: ["dashboard-product", productId],
    queryFn: () => api.get<ProductDetail>(`/vendors/dashboard/products/${productId}`),
    enabled: !!productId,
  });
}
